import {
  Box,
  Typography,
  Alert,
  Paper,
  IconButton,
  Grid,
  Chip,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  Tooltip,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import CakeIcon from "@mui/icons-material/Cake";
import { useGetAbsenceRequestsForCalendarQuery } from "../../api/absenceRequestsApi";
import { useGetEmployeeBirthdaysForMonthQuery } from "../../api/employeesApi";
import { LoadingSpinner } from "../../components/common/LoadingSpinner";
import PageTitle from "../../components/common/PageTitle";
import { AbsenceIcon } from "../../components/common/pageIcons";
import { ViewAbsenceRequestDialog } from "./components/ViewAbsenceRequestDialog";
import { CreatePreApprovedAbsenceDialog } from "./components/CreatePreApprovedAbsenceDialog";
import { AbsenceRequestResponseDto } from "../../types/absence";
import { AbsenceRequestStatus, getAbsenceCategoryColor } from "../../constants/absenceEnums";

const AdminCalendarPage: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [requestToView, setRequestToView] = useState<AbsenceRequestResponseDto | null>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Set<number>>(new Set());
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [showBirthdays, setShowBirthdays] = useState(true);

  // Get first and last day of current month
  const startDate = useMemo(() => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    return date.toISOString();
  }, [currentDate]);

  const endDate = useMemo(() => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    return date.toISOString();
  }, [currentDate]);

  const { data: requests, isLoading, error, refetch } = useGetAbsenceRequestsForCalendarQuery({
    startDate,
    endDate,
    status: AbsenceRequestStatus.Approved, // Only show approved absences
  });

  const { data: birthdays = [] } = useGetEmployeeBirthdaysForMonthQuery({
    year: currentDate.getFullYear(),
    month: currentDate.getMonth() + 1,
  });

  // Extract unique employees from requests
  const employees = useMemo(() => {
    if (!requests) return [];
    const uniqueEmployees = new Map<number, { id: number; name: string }>();
    requests.forEach((req) => {
      if (req.employeeId && req.employeeName && !uniqueEmployees.has(req.employeeId)) {
        uniqueEmployees.set(req.employeeId, {
          id: req.employeeId,
          name: req.employeeName,
        });
      }
    });
    return Array.from(uniqueEmployees.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [requests]);

  // Auto-select all employees on first load
  React.useEffect(() => {
    if (employees.length > 0 && selectedEmployeeIds.size === 0) {
      setSelectedEmployeeIds(new Set(employees.map((emp) => emp.id)));
    }
  }, [employees]);

  // Filter requests based on selected employees
  const filteredRequests = useMemo(() => {
    if (!requests) return [];
    if (selectedEmployeeIds.size === 0) return []; // Show nothing if none selected
    return requests.filter((req) => selectedEmployeeIds.has(req.employeeId));
  }, [requests, selectedEmployeeIds]);

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleViewDetails = (request: AbsenceRequestResponseDto) => {
    setRequestToView(request);
  };

  const handleCreateSuccess = () => {
    refetch();
  };

  const handleEmployeeToggle = (employeeId: number) => {
    setSelectedEmployeeIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(employeeId)) {
        newSet.delete(employeeId);
      } else {
        newSet.add(employeeId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedEmployeeIds(new Set(employees.map((emp) => emp.id)));
  };

  const handleDeselectAll = () => {
    setSelectedEmployeeIds(new Set());
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // First day of month
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday
    
    // Last day of month
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    // Days from previous month
    const prevMonthDays = firstDayOfWeek;
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    
    const days: Array<{
      date: Date;
      isCurrentMonth: boolean;
      requests: AbsenceRequestResponseDto[];
    }> = [];
    
    // Previous month days
    for (let i = prevMonthDays - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({
        date,
        isCurrentMonth: false,
        requests: [],
      });
    }
    
    // Current month days
    for (let i = 1; i <= totalDays; i++) {
      const date = new Date(year, month, i);
      const dayRequests = filteredRequests?.filter((req) => {
        const reqStart = new Date(req.startDate);
        const reqEnd = new Date(req.endDate);
        reqStart.setHours(0, 0, 0, 0);
        reqEnd.setHours(0, 0, 0, 0);
        date.setHours(0, 0, 0, 0);
        return date >= reqStart && date <= reqEnd;
      }) || [];
      
      days.push({
        date,
        isCurrentMonth: true,
        requests: dayRequests,
      });
    }
    
    // Next month days to complete the grid (42 days = 6 weeks)
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        date,
        isCurrentMonth: false,
        requests: [],
      });
    }
    
    return days;
  }, [currentDate, filteredRequests]);

  const monthName = currentDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load calendar data. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: "flex", gap: 3, height: "100vh", overflow: "hidden" }}>
      {/* Sidebar - Employee Filter */}
      <Paper sx={{ width: 280, p: 2, flexShrink: 0, overflowY: "auto", maxHeight: "100%" }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Employees
        </Typography>
        
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleSelectAll}
            fullWidth
          >
            Select All
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleDeselectAll}
            fullWidth
          >
            Clear
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Show Birthdays Filter */}
        <FormControlLabel
          control={
            <Checkbox
              checked={showBirthdays}
              onChange={(e) => setShowBirthdays(e.target.checked)}
              size="small"
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CakeIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                Show Birthdays
              </Typography>
            </Box>
          }
          sx={{ mb: 2 }}
        />

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {employees.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No employees with absences this month
            </Typography>
          ) : (
            employees.map((employee) => (
              <FormControlLabel
                key={employee.id}
                control={
                  <Checkbox
                    checked={selectedEmployeeIds.has(employee.id)}
                    onChange={() => handleEmployeeToggle(employee.id)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                    {employee.name}
                  </Typography>
                }
              />
            ))
          )}
        </Box>
      </Paper>

      {/* Main Calendar Content */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
        {/* Header */}
        <PageTitle
          title="Absence Calendar"
          icon={<AbsenceIcon />}
          sx={{ flexShrink: 0 }}
          actions={
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsCreateDialogOpen(true)}
                sx={{ textTransform: "none" }}
              >
                Create Absence for Employee
              </Button>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <IconButton onClick={handlePreviousMonth} size="small">
                  <ChevronLeftIcon />
                </IconButton>
                <Typography
                  variant="subtitle1"
                  sx={{
                    minWidth: 180,
                    textAlign: "center",
                    fontWeight: 500,
                  }}
                >
                  {monthName}
                </Typography>
                <IconButton onClick={handleNextMonth} size="small">
                  <ChevronRightIcon />
                </IconButton>
                <Box
                  component="button"
                  onClick={handleToday}
                  sx={{
                    ml: 2,
                    px: 2,
                    py: 0.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    backgroundColor: "background.paper",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  Today
                </Box>
              </Box>
            </Box>
          }
        />

        {/* Calendar */}
        <Paper sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Week day headers */}
          <Grid container spacing={1} sx={{ mb: 1, flexShrink: 0 }}>
            {weekDays.map((day) => (
              <Grid item xs={12 / 7} key={day}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Calendar grid - Scrollable */}
          <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
            <Grid container spacing={1} sx={{ margin: 0, width: "100%" }}>
            {calendarDays.map((day, index) => {
              const isToday =
                day.date.toDateString() === new Date().toDateString();
              const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;

              return (
                <Grid item xs={12 / 7} key={index} sx={{ display: "flex" }}>
                  <Paper
                    sx={{
                      width: "100%",
                      minHeight: 140,
                      p: 1,
                      backgroundColor: day.isCurrentMonth
                        ? "background.paper"
                        : "action.disabledBackground",
                      border: isToday ? "2px solid" : "1px solid",
                      borderColor: isToday ? "primary.main" : "divider",
                      cursor: day.requests.length > 0 ? "default" : "auto",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: isToday ? 700 : day.isCurrentMonth ? 500 : 400,
                        color: isToday
                          ? "primary.main"
                          : day.isCurrentMonth
                          ? "text.primary"
                          : "text.disabled",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      {day.date.getDate()}
                    </Typography>

                    {/* Absence chips */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, flex: 1, overflow: "hidden" }}>
                      {day.requests.slice(0, 5).map((request) => {
                        const color = getAbsenceCategoryColor(request.absenceCategory);
                        return (
                          <Chip
                            key={request.id}
                            label={request.employeeName || "Unknown"}
                            size="small"
                            onClick={() => handleViewDetails(request)}
                            style={{
                              backgroundColor: color,
                              color: "#fff",
                            }}
                            sx={{
                              fontSize: "0.6rem",
                              height: 18,
                              cursor: "pointer",
                              "&:hover": {
                                opacity: 0.85,
                              },
                              "& .MuiChip-label": {
                                px: 0.4,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                              },
                            }}
                          />
                        );
                      })}
                      {day.requests.length > 5 && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.6rem",
                            color: "text.secondary",
                            pl: 0.5,
                            fontWeight: 500,
                            backgroundColor: "action.hover",
                            borderRadius: 1,
                            px: 0.5,
                            py: 0.25,
                            textAlign: "center"
                          }}
                        >
                          +{day.requests.length - 5} more
                        </Typography>
                      )}

                      {/* Birthday chips */}
                      {showBirthdays && birthdays
                        .filter((birthday) => {
                          const birthdayDate = new Date(birthday.birthdayDate);
                          return birthdayDate.getDate() === day.date.getDate() &&
                                 birthdayDate.getMonth() === day.date.getMonth() &&
                                 birthdayDate.getFullYear() === day.date.getFullYear();
                        })
                        .map((birthday) => (
                          <Tooltip
                            key={birthday.employeeId}
                            title={`${birthday.employeeName} (${birthday.age} years old)`}
                            arrow
                          >
                            <Chip
                              icon={<CakeIcon />}
                              label={birthday.employeeName}
                              size="small"
                              style={{
                                backgroundColor: "#FF69B4",
                                color: "#fff",
                              }}
                              sx={{
                                fontSize: "0.6rem",
                                height: 18,
                                cursor: "pointer",
                                "&:hover": {
                                  opacity: 0.85,
                                },
                                "& .MuiChip-label": {
                                  px: 0.4,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                },
                                "& .MuiChip-icon": {
                                  color: "#fff",
                                  fontSize: "0.8rem",
                                  ml: 0.5,
                                },
                              }}
                            />
                          </Tooltip>
                        ))}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
            </Grid>
          </Box>

          {/* Legend */}
          <Box sx={{ pt: 2, mt: 2, borderTop: 1, borderColor: "divider", flexShrink: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Legend
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: getAbsenceCategoryColor(1),
                  }}
                />
                <Typography variant="caption">Vacation</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: getAbsenceCategoryColor(2),
                  }}
                />
                <Typography variant="caption">Sick Days</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: getAbsenceCategoryColor(3),
                  }}
                />
                <Typography variant="caption">Medical Leave</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: getAbsenceCategoryColor(5),
                  }}
                />
                <Typography variant="caption">Maternity</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: getAbsenceCategoryColor(6),
                  }}
                />
                <Typography variant="caption">Parental</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: getAbsenceCategoryColor(7),
                  }}
                />
                <Typography variant="caption">Family Holiday</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: getAbsenceCategoryColor(8),
                  }}
                />
                <Typography variant="caption">Early Leave</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: getAbsenceCategoryColor(9),
                  }}
                />
                <Typography variant="caption">Late On Shift</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: "#FF69B4",
                  }}
                />
                <Typography variant="caption">Birthday</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* View Request Details Dialog */}
      <ViewAbsenceRequestDialog
        open={!!requestToView}
        request={requestToView}
        onClose={() => setRequestToView(null)}
      />

      {/* Create Pre-Approved Absence Dialog */}
      <CreatePreApprovedAbsenceDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </Box>
  );
};

export default AdminCalendarPage;

